import type { Area, CourierDeliveryStatus, CurrencyCode } from 'common/api/api-types';

import { getHead, getTail } from 'common/components/deliveryStatusUtils';
import currencyCodeToSymbol from 'common/currencyCodeToSymbol';
import { getTimeInHoursDisplay } from 'common/utils/timeUtils';

export type CsvColumn = {
  id: string;
  name: string;
};

export const getName = (value?: string): string => {
  return value ?? '';
};

export const getTeamName = (team?: string, areas?: Area[]): string | undefined => {
  return team ? (areas?.find(area => area.name === team)?.printedName ?? team) : undefined;
};

export const getDeliveryStatus = ({
  courierStatusUpdatedAt,
  deliveryStatus,
  prefixIdle,
}: {
  courierStatusUpdatedAt?: Date;
  deliveryStatus?: CourierDeliveryStatus;
  prefixIdle?: string;
}): string => {
  return (
    getHead({ deliveryStatus, prefixIdle }) + getTail({ deliveryStatus, courierStatusUpdatedAt })
  );
};

export const getNumberOfDeliveries = (
  integerFormat?: Intl.NumberFormat,
  value?: number,
): string => {
  return integerFormat && value !== undefined ? integerFormat.format(value) : '-';
};

export const getTaskAcceptanceRate = (
  percentFormat?: Intl.NumberFormat,
  value?: number,
): string => {
  return percentFormat && value !== undefined ? percentFormat.format(value) : '-';
};

export const getTaskCompletionRate = (
  percentFormat?: Intl.NumberFormat,
  value?: number,
): string => {
  return percentFormat && value !== undefined ? percentFormat.format(value) : '-';
};

export const getDeliveryPerHour = ({
  decimalFormatWith2DecimalPlaces,
  onlineHours = 0,
  value,
}: {
  decimalFormatWith2DecimalPlaces?: Intl.NumberFormat;
  onlineHours?: number;
  value?: number;
}): string => {
  if (decimalFormatWith2DecimalPlaces && onlineHours >= 1 && value !== undefined) {
    return decimalFormatWith2DecimalPlaces.format(value);
  }

  return '-';
};

export const getTotalOnlineTime = (value?: number) => {
  return value !== undefined ? getTimeInHoursDisplay(value) : '-';
};

export const getTotalIdleTime = (value?: number) => {
  return value !== undefined ? getTimeInHoursDisplay(value) : '-';
};

export const getTotalOnTaskTime = (value?: number) => {
  return value !== undefined ? getTimeInHoursDisplay(value) : '-';
};

export const getTravelledDistance = (integerFormat?: Intl.NumberFormat, value?: number): string => {
  return integerFormat && value !== undefined ? integerFormat.format(value) : '-';
};

export const getOnTaskTravelledDistance = (
  integerFormat?: Intl.NumberFormat,
  value?: number,
): string => {
  return integerFormat && value !== undefined ? integerFormat.format(value) : '-';
};

export const getCashBalance = ({
  decimalFormat,
  amount,
  currencyCode,
}: {
  decimalFormat?: Intl.NumberFormat;
  amount?: number;
  currencyCode?: CurrencyCode;
}): string => {
  return amount !== undefined && decimalFormat
    ? (currencyCodeToSymbol(currencyCode) ?? '') + decimalFormat.format(amount / 100)
    : '-';
};

export const getMaxWeeklyHours = (value?: number): string => {
  return value !== undefined ? String(value) : '-';
};

export const getDistinctActiveTime = ({
  deliveries,
  onTaskHours,
}: {
  deliveries?: number;
  onTaskHours?: number;
}) =>
  deliveries != null &&
  onTaskHours != null &&
  deliveries > 0 &&
  Number.isFinite(deliveries) &&
  Number.isFinite(onTaskHours)
    ? onTaskHours / deliveries
    : undefined;

export const getTasksShown = (integerFormat?: Intl.NumberFormat, value?: number): string => {
  return integerFormat && value !== undefined ? integerFormat.format(value) : '-';
};

export const createNumberFormatter =
  (formatter: Intl.NumberFormat | undefined) =>
  (value: number | undefined): string | undefined => {
    return formatter && value !== undefined ? formatter.format(value) : undefined;
  };
